
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Project, AgentResult, Company, ChatSession, Message, AppSettings, AIProvider, CostMode, GeminiModel, LiveVoiceProvider, TTSProvider, EmailDraft, SelectedSkill, CallTranscriptPart } from './types';
import { AGENT_DEFINITIONS, runAgent } from './services/agentRegistry';
import { parseCSV } from './services/csvParser';
import { prepareLiveAudioContext } from './services/liveAudio';
import { AutopilotEngine } from './services/autopilotEngine';
import { processFileAttachment, readFileContent } from './services/fileHandler';
import NeuralHero from './components/NeuralHero';
import { AgentCard } from './components/AgentCard';
import { ResultViewer } from './components/ResultViewer';
import ChatWindow from './components/ChatWindow';
import Sidebar from './components/Sidebar';
import VoiceInterface from './components/VoiceInterface';
import SkillModal from './components/modals/SkillModal';
import { CompanyDetailView } from './components/CompanyDetailView';
import { LeadFinderModeModal } from './components/modals/LeadFinderModeModal';
import { ResearchModal } from './components/modals/ResearchModal';
import { EmailComposerModal } from './components/modals/EmailComposerModal';
import SettingsModal from './components/modals/SettingsModal';
import {
  PlusIcon, SettingsIcon, BookIcon,
  UserCircleIcon, CsvIcon, XIcon, PencilIcon
} from './components/Icons';
import { applyUiTheme, DEFAULT_SETTINGS, normalizeSettings, SettingsService } from './services/settingsService';
import { GeminiService, decodeBase64, decodeAudioData as decodePCMAudio } from './services/geminiService';
import { OpenRouterService } from './services/openRouterService';
import { LocalLLMService } from './services/localLLMService';
import { ProjectExportService } from './services/projectExportService';
import { MemoryService } from './services/memoryService';
import { EmailAgentService } from './services/emailAgent';
import { CallAnalysisService } from './services/callAnalysisService';
import { getItem, setItem } from './services/db';
import { WorkspaceSyncService } from './services/workspaceSyncService';
import { Skill } from './services/skillService';

export default function App() {
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, AgentResult[]>>({});
  const [loading, setLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const audioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const [globalLoadingText, setGlobalLoadingText] = useState<string | null>(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [isEditingProject, setIsEditingProject] = useState(false);

  const [projectName, setProjectName] = useState('');
  const [projectDesc, setProjectDesc] = useState('');
  const [projectSenderEmail, setProjectSenderEmail] = useState('');
  const [projectSenderContactBlock, setProjectSenderContactBlock] = useState('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [clientPhotoFile, setClientPhotoFile] = useState<File | null>(null);
  const [kbFiles, setKbFiles] = useState<File[]>([]);
  const [isProcessingFile, setIsProcessingFile] = useState(false);

  // Autopilot State for New/Edit Project
  const [autopilotEnabled, setAutopilotEnabled] = useState(false);

  const clientPhotoInputRef = useRef<HTMLInputElement>(null);
  const kbFilesInputRef = useRef<HTMLInputElement>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);

  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showLeadFinderMode, setShowLeadFinderMode] = useState(false);
  const [showSkillModal, setShowSkillModal] = useState(false);
  const [showResearchModal, setShowResearchModal] = useState(false);
  const [showTechnicalChat, setShowTechnicalChat] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [technicalChatWidth, setTechnicalChatWidth] = useState(980);
  const [isResizingTechnicalChat, setIsResizingTechnicalChat] = useState(false);
  const [chatDossierTargetId, setChatDossierTargetId] = useState<string | null>(null);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [activeVoiceContext, setActiveVoiceContext] = useState<{ company: Company | null, strategy?: any } | null>(null);

  const [settings, setSettings] = useState<AppSettings>(() => {
    return SettingsService.getSettings();
  });

  useEffect(() => {
    applyUiTheme(settings.uiTheme);
  }, [settings.uiTheme]);

  // Centralized Settings Sync Logic
  // Only sync from LocalStorage ONCE on initial load to avoid overwriting state
  useEffect(() => {
    const globalSet = SettingsService.getSettings();
    if (activeProjectId) {
      const proj = projects.find(p => p.id === activeProjectId);
      if (proj?.settings) {
        setSettings(normalizeSettings({ 
          ...proj.settings, 
          geminiKey: globalSet.geminiKey || proj.settings.geminiKey, 
          openRouterKey: globalSet.openRouterKey || proj.settings.openRouterKey,
          testEmailAddress: globalSet.testEmailAddress || proj.settings.testEmailAddress
        }));
      } else {
        setSettings(normalizeSettings(globalSet));
      }
    } else {
      setSettings(normalizeSettings(globalSet));
    }
  }, [activeProjectId]); // Only trigger on project change

  const [assistantSession, setAssistantSession] = useState<ChatSession>({
    id: 'assistant',
    projectId: null,
    name: 'BDAT AI Assistant',
    messages: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });

  // Assistant session auto-save
  useEffect(() => {
    if (!isDataLoaded) return;
    setItem('bdat_assistant_session', assistantSession).catch(console.error);
    setItem('bdat_projects', projects).catch(console.error);
  }, [assistantSession, projects, isDataLoaded]);
  
  const handleNewChat = () => {
    setAssistantSession(prev => ({
      ...prev,
      messages: [],
      updatedAt: new Date().toISOString()
    }));
  };

  const handleSaveSelectedSkills = (skills: Skill[]) => {
    if (!activeProject) return;

    const normalizedSkills: SelectedSkill[] = skills.map((skill) => ({
      id: skill.id,
      name: skill.name,
      category: skill.category,
      path: skill.path,
      description: skill.description,
    }));

    persistProjects(
      projects.map((project) =>
        project.id === activeProject.id
          ? { ...project, selectedSkills: normalizedSkills }
          : project
      )
    );
  };

  const handleCopyChatResearchToDossier = (message: Message) => {
    if (!activeProject || !chatDossierTargetId) {
      alert('Kies eerst een dossierdoel in de chatcontext.');
      return;
    }

    const company = activeProject.customData?.find((item) => item.id === chatDossierTargetId);
    if (!company) {
      alert('Doeldossier niet gevonden.');
      return;
    }

    MemoryService.addInteraction(company.id, activeProject.id, {
      id: crypto.randomUUID(),
      type: 'system_log',
      direction: 'inbound',
      timestamp: new Date().toISOString(),
      summary: `Chat research toegevoegd aan dossier van ${company.name}`,
      sentiment: 'neutral',
      details: {
        content: message.content,
        sources: message.sources || [],
      },
    });

    setMemoryRefreshKey((current) => current + 1);
    alert(`Chat research opgeslagen in dossier van ${company.name}.`);
  };

  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [emailComposerState, setEmailComposerState] = useState<{ company: Company; draft: EmailDraft } | null>(null);
  const [isSendingEmailDraft, setIsSendingEmailDraft] = useState(false);
  const [memoryRefreshKey, setMemoryRefreshKey] = useState(0);
  const hasBootstrappedRemoteRef = useRef(false);
  const activeProject = projects.find(p => p.id === activeProjectId) || null;
  const activeSkills = activeProject?.selectedSkills || [];
  const activeResults = useMemo(() => {
    if (!activeProjectId) return [];
    return results[activeProjectId] || [];
  }, [results, activeProjectId]);

  useEffect(() => {
    if (!activeProject) {
      setChatDossierTargetId(null);
      return;
    }

    const nextTargetExists = activeProject.customData?.some((company) => company.id === chatDossierTargetId);
    if (!nextTargetExists) {
      setChatDossierTargetId(activeProject.customData?.[0]?.id || null);
    }
  }, [activeProjectId, activeProject?.customData]);

  const handleTechnicalChatResizeMove = useCallback((event: MouseEvent) => {
    const maxWidth = Math.max(360, window.innerWidth - 24);
    const nextWidth = Math.min(maxWidth, Math.max(360, window.innerWidth - event.clientX - 16));
    setTechnicalChatWidth(nextWidth);
  }, []);

  useEffect(() => {
    const clampTechnicalChat = () => {
      const maxWidth = Math.max(360, window.innerWidth - 24);
      const suggestedWidth = Math.min(maxWidth, Math.max(420, Math.round(window.innerWidth * 0.56)));
      setTechnicalChatWidth((current) => Math.min(maxWidth, current || suggestedWidth));
    };

    clampTechnicalChat();
    window.addEventListener('resize', clampTechnicalChat);
    return () => window.removeEventListener('resize', clampTechnicalChat);
  }, []);

  const stopTechnicalChatResize = useCallback(() => {
    setIsResizingTechnicalChat(false);
    window.removeEventListener('mousemove', handleTechnicalChatResizeMove);
    window.removeEventListener('mouseup', stopTechnicalChatResize);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [handleTechnicalChatResizeMove]);

  const startTechnicalChatResize = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsResizingTechnicalChat(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleTechnicalChatResizeMove);
    window.addEventListener('mouseup', stopTechnicalChatResize);
  }, [handleTechnicalChatResizeMove, stopTechnicalChatResize]);

  useEffect(() => {
    return () => {
      stopTechnicalChatResize();
    };
  }, [stopTechnicalChatResize]);

  const resolveCompanyReference = (companyLike: Partial<Company> & Record<string, any>): Company | null => {
    if (!companyLike) return null;

    const candidateId = companyLike.id || companyLike.companyId;
    const candidateName = companyLike.name || companyLike.companyName;
    const normalizedCandidateName = candidateName?.trim().toLowerCase();

    const matchedCompany = activeProject?.customData?.find((company) => {
      if (candidateId && company.id === candidateId) return true;
      if (normalizedCandidateName && company.name.trim().toLowerCase() === normalizedCandidateName) return true;
      return false;
    });

    if (!matchedCompany && !candidateId && !candidateName) {
      return null;
    }

    return {
      id: matchedCompany?.id || candidateId || crypto.randomUUID(),
      name: matchedCompany?.name || candidateName || 'Onbekend Bedrijf',
      industry: companyLike.industry || matchedCompany?.industry || 'Lead',
      description: companyLike.description || companyLike.summary || companyLike.reason || matchedCompany?.description || '',
      website: companyLike.website || companyLike.contactInfo?.website || matchedCompany?.website || '',
      location: companyLike.location || companyLike.contactInfo?.address || matchedCompany?.location || '',
      email: companyLike.email || companyLike.contactInfo?.email || matchedCompany?.email || '',
      phone: companyLike.phone || companyLike.contactInfo?.phone || matchedCompany?.phone || '',
      contactName: companyLike.contactName || companyLike.contactInfo?.keyContact || matchedCompany?.contactName
    };
  };

  const getSkillContextBlock = (skills: SelectedSkill[] = activeSkills) => {
    if (!skills.length) return '';

    return `\nACTIEVE SKILLS:\n${skills
      .map((skill) => `- ${skill.name} (${skill.category}): ${skill.description || skill.path}`)
      .join('\n')}`;
  };

  const handleSelectCompany = (companyLike: Partial<Company> & Record<string, any>) => {
    const resolvedCompany = resolveCompanyReference(companyLike);
    if (!resolvedCompany) return;
    setSelectedCompany(resolvedCompany);
  };

  const handleSendEmailDraft = async (company: Company, draft: EmailDraft) => {
    if (!activeProject) return;

    setIsSendingEmailDraft(true);
    try {
      const sent = await EmailAgentService.sendEmail(activeProject, company, draft);
      if (!sent) return;

      setMemoryRefreshKey((current) => current + 1);
      alert(`Mail opgeslagen voor ${company.name}.`);
      setEmailComposerState(null);
    } catch (error) {
      console.error(error);
      alert("Mail verzenden mislukt.");
    } finally {
      setIsSendingEmailDraft(false);
    }
  };

  useEffect(() => {
    async function loadData() {
      try {
        const savedProjects = await getItem<Project[]>('bdat_projects');
        const savedResults = await getItem<Record<string, AgentResult[]>>('bdat_results');
        const savedAssistant = await getItem<ChatSession>('bdat_assistant_session');

        if (savedProjects) setProjects(savedProjects);
        if (savedResults) setResults(savedResults);
        if (savedAssistant) setAssistantSession(savedAssistant);
      } catch (err) {
        console.error("Failed to load db data", err);
      } finally {
        setIsDataLoaded(true);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    MemoryService.configurePersistenceAdapter({
      loadMemory: (companyId, projectId) => WorkspaceSyncService.loadMemory(projectId, companyId),
      saveMemory: (memory) => WorkspaceSyncService.saveMemory(memory.projectId, memory.companyId, memory),
    });
  }, []);

  useEffect(() => {
    if (!isDataLoaded || hasBootstrappedRemoteRef.current) return;

    let cancelled = false;

    const bootstrapFromDatabase = async () => {
      try {
        const remoteWorkspaces = await WorkspaceSyncService.loadWorkspaces();
        if (cancelled || remoteWorkspaces.length === 0) return;

        remoteWorkspaces.forEach((workspace) => {
          Object.entries(workspace.memories || {}).forEach(([companyId, memory]) => {
            MemoryService.hydrateMemory(companyId, workspace.project.id, memory);
          });
        });

        const knownProjectIds = new Set(projects.map((project) => project.id));
        const mergedProjects = [...projects];
        let projectsChanged = false;

        remoteWorkspaces.forEach((workspace) => {
          if (!knownProjectIds.has(workspace.project.id)) {
            mergedProjects.push(workspace.project);
            knownProjectIds.add(workspace.project.id);
            projectsChanged = true;
          }
        });

        const mergedResults = { ...results };
        let resultsChanged = false;

        remoteWorkspaces.forEach((workspace) => {
          if (!mergedResults[workspace.project.id] || mergedResults[workspace.project.id].length === 0) {
            mergedResults[workspace.project.id] = workspace.results || [];
            resultsChanged = true;
          }
        });

        if (projectsChanged) {
          persistProjects(mergedProjects);
        }

        if (resultsChanged) {
          persistResults(mergedResults);
        }

        if (!activeProjectId && (mergedProjects[0] || remoteWorkspaces[0]?.project)) {
          setActiveProjectId(mergedProjects[0]?.id || remoteWorkspaces[0].project.id);
        }
      } catch (error) {
        console.warn('[Sync] Remote bootstrap skipped', error);
      } finally {
        hasBootstrappedRemoteRef.current = true;
      }
    };

    void bootstrapFromDatabase();

    return () => {
      cancelled = true;
    };
  }, [isDataLoaded, projects.length, activeProjectId]);

  useEffect(() => {
    if (!isDataLoaded || !hasBootstrappedRemoteRef.current || projects.length === 0) return;

    const timeoutId = window.setTimeout(() => {
      projects.forEach((project) => {
        void WorkspaceSyncService.saveWorkspace(
          project,
          results[project.id] || [],
          MemoryService.exportProjectMemories(project)
        ).catch((error) => {
          console.warn(`[Sync] Workspace save failed for ${project.name}`, error);
        });
      });
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [projects, results, isDataLoaded]);

  const persistProjects = (newProjects: Project[]) => {
    setProjects(newProjects);
    if (isDataLoaded) setItem('bdat_projects', newProjects).catch(console.error);
  };

  const persistResults = (newResults: Record<string, AgentResult[]>) => {
    setResults(newResults);
    if (isDataLoaded) setItem('bdat_results', newResults).catch(console.error);
  };

  const extractEmailFromText = (value: string) => {
    const match = value.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    return match?.[0] || '';
  };

  const resetProjectModal = () => {
    setProjectName('');
    setProjectDesc('');
    setProjectSenderEmail('');
    setProjectSenderContactBlock('');
    setKbFiles([]);
    setClientPhotoFile(null);
    setCsvFile(null);
    setAutopilotEnabled(false);
    setIsEditingProject(false);
    setShowProjectModal(false);
  };

  const handleSaveProject = async () => {
    if (!projectName) return;
    setIsProcessingFile(true);
    try {
      let customData = isEditingProject ? (activeProject?.customData || []) : [];
      let attachments = isEditingProject ? (activeProject?.attachments || []) : [];
      let clientPhoto = isEditingProject ? activeProject?.clientPhoto : undefined;

      // Autopilot Configuration
      const autopilotSettings = {
        isEnabled: autopilotEnabled,
        rules: AutopilotEngine.getDefaultRules().map(r => ({ ...r, enabled: autopilotEnabled })) // Enable default rules if global toggle is on
      };

      let newCompanies: Company[] = [];

      if (csvFile) {
        const text = await readFileContent(csvFile);
        newCompanies = parseCSV(text);
        customData = [...customData, ...newCompanies];
      }
      if (kbFiles.length > 0) {
        const newAttachments = await Promise.all(kbFiles.map(f => processFileAttachment(f)));
        attachments = [...attachments, ...newAttachments];
      }
      if (clientPhotoFile) clientPhoto = await processFileAttachment(clientPhotoFile);

      let finalProjectId = activeProject?.id;

      if (isEditingProject && activeProject) {
        const updatedProject: Project = {
          ...activeProject,
          name: projectName,
          description: projectDesc,
          senderEmail: projectSenderEmail.trim() || extractEmailFromText(projectDesc),
          senderContactBlock: projectSenderContactBlock.trim(),
          customData,
          attachments,
          clientPhoto,
          settings: activeProject.settings || DEFAULT_SETTINGS,
          autopilot: autopilotSettings
        };
        persistProjects(projects.map(p => p.id === activeProject.id ? updatedProject : p));
      } else {
        const newProject: Project = {
          id: crypto.randomUUID(),
          name: projectName,
          description: projectDesc,
          senderEmail: projectSenderEmail.trim() || extractEmailFromText(projectDesc),
          senderContactBlock: projectSenderContactBlock.trim(),
          createdAt: new Date().toISOString(),
          customData,
          attachments,
          clientPhoto,
          settings: DEFAULT_SETTINGS,
          autopilot: autopilotSettings
        };
        persistProjects([...projects, newProject]);
        setActiveProjectId(newProject.id);
        finalProjectId = newProject.id;
      }

      // Initialiseer geheugen voor nieuwe CSV bedrijven
      if (newCompanies.length > 0 && finalProjectId) {
        newCompanies.forEach(c => MemoryService.initMemory(c.id, finalProjectId!, 'CSV Import'));
      }

      resetProjectModal();
    } catch (e) {
      console.error(e);
      alert("Opslaan mislukt. Controleer de bestanden.");
    } finally {
      setIsProcessingFile(false);
    }
  };

  const handleRunAgent = async (agentId: string, options?: any) => {
    if (!activeProject) return;

    if (agentId === 'lead-finder' && !options?.mode) {
      setShowLeadFinderMode(true);
      return;
    }

    if (agentId === 'researcher' && !options?.companyName) {
      setShowResearchModal(true);
      return;
    }

    setLoading(true);
    let loadMsg = "Agent Context Initialiseren...";
    if (agentId.includes('researcher')) loadMsg = "Analyseren en verifiëren...";
    if (options?.mode === 'competitor') loadMsg = "Concurrentie Zoeken...";

    setGlobalLoadingText(loadMsg);

    try {
      const result = await runAgent(agentId, activeProject, {
        ...options,
        selectedSkills: activeSkills,
      });

      const updatedCustomData = [...(activeProject.customData || [])];
      let targetCompanyId = options?.companyId;

      // 1. DATA SYNC LOGICA
      if (agentId === 'lead-finder' && Array.isArray(result.data)) {
        result.data.forEach((lead: any) => {
          if (!updatedCustomData.find(c => c.name === lead.companyName || (lead.companyId && c.id === lead.companyId))) {
            const newCompany = {
              id: lead.companyId || crypto.randomUUID(),
              name: lead.companyName,
              description: lead.reason || '',
              industry: lead.industry || 'Lead',
              website: lead.website || '',
              location: lead.location || '',
              email: lead.email || lead.contactInfo?.email || '',
              phone: lead.phone || lead.contactInfo?.phone || ''
            };
            updatedCustomData.push(newCompany);
          }
        });
      } else if (result.data.companyName) {
        const idx = updatedCustomData.findIndex(c =>
          c.name === result.data.companyName ||
          (result.data.companyId && c.id === result.data.companyId)
        );
        const enrichedCompany = {
          id: result.data.companyId || (idx > -1 ? updatedCustomData[idx].id : crypto.randomUUID()),
          name: result.data.companyName,
          industry: result.data.industry || (idx > -1 ? updatedCustomData[idx].industry : 'Unknown'),
          description: result.data.summary || (idx > -1 ? updatedCustomData[idx].description : ''),
          website: result.data.contactInfo?.website || (idx > -1 ? updatedCustomData[idx].website : ''),
          location: result.data.contactInfo?.address || (idx > -1 ? updatedCustomData[idx].location : ''),
          email: result.data.contactInfo?.email || (idx > -1 ? updatedCustomData[idx].email : ''),
          phone: result.data.contactInfo?.phone || (idx > -1 ? updatedCustomData[idx].phone : ''),
          contactName: result.data.contactInfo?.keyContact
        };

        targetCompanyId = enrichedCompany.id;
        if (idx > -1) updatedCustomData[idx] = enrichedCompany;
        else updatedCustomData.unshift(enrichedCompany);
      }

      // 2. UI STATE UPDATE
      const newResults = {
        ...results,
        [activeProject.id]: [result, ...(results[activeProject.id] || [])]
      };
      persistResults(newResults);

      const updatedProject = { ...activeProject, customData: updatedCustomData };
      persistProjects(projects.map(p => p.id === activeProject.id ? updatedProject : p));

      if (agentId === 'email-outreach') {
        const emailTargetCompany =
          updatedCustomData.find((company) => company.id === options?.companyId) ||
          resolveCompanyReference({ id: options?.companyId }) ||
          selectedCompany;

        if (emailTargetCompany) {
          setEmailComposerState({
            company: emailTargetCompany,
            draft: result.data as EmailDraft
          });
        }
      }

      // Update selected company if it was the target of research
      if (selectedCompany && targetCompanyId === selectedCompany.id) {
        const enriched = updatedCustomData.find(c => c.id === targetCompanyId);
        if (enriched) setSelectedCompany(enriched);
      }

      // 4. AUTOPILOT TRIGGER
      // We pass a fresh function ref for onRunAgent to avoid recursion issues with stale state,
      // but essentially we just call handleRunAgent recursively.
      // Note: We need to use 'updatedProject' because state update hasn't happened yet.
      setTimeout(() => {
        AutopilotEngine.processResult(updatedProject, result,
          (p) => persistProjects(projects.map(proj => proj.id === p.id ? p : proj)),
          (id, opts) => handleRunAgent(id, opts)
        );
      }, 500); // Small delay to allow UI to settle

    } catch (e) {
      console.error(e);
      const message = e instanceof Error && e.message ? e.message : "Agent uitvoering mislukt.";
      alert(`Agent uitvoering mislukt: ${message}`);
    } finally {
      setLoading(false);
      setGlobalLoadingText(null);
      setShowLeadFinderMode(false);
      setShowResearchModal(false);
    }
  };

  const handleGenerateStrategy = async (researchData: any) => {
    if (!activeProject) return;
    setLoading(true);
    setGlobalLoadingText("Strategie formuleren...");
    try {
      const result = await runAgent('strategy-agent', activeProject, {
        researchData,
        selectedSkills: activeSkills,
      });

      // Update Memory FIRST
      const company = activeProject.customData?.find(c => c.name === researchData.companyName);
      if (company) {
        MemoryService.addInteraction(company.id, activeProject.id, {
          id: crypto.randomUUID(),
          type: 'system_log',
          direction: 'inbound',
          timestamp: new Date().toISOString(),
          summary: `Strategie gegenereerd: ${result.data.strategyTitle}`,
          sentiment: 'neutral',
          details: result.data
        });
      }

      // Then update UI
      const newResults = {
        ...results,
        [activeProject.id]: [result, ...(results[activeProject.id] || [])]
      };
      persistResults(newResults);

    } catch (e) { console.error(e); } finally { setLoading(false); setGlobalLoadingText(null); }
  };

  const handleSaveLead = (leadData: any) => {
    if (!activeProject) return;
    const company: Company = {
      id: leadData.companyId || crypto.randomUUID(),
      name: leadData.companyName,
      industry: leadData.industry || 'Lead',
      description: leadData.summary || leadData.reason || '',
      website: leadData.contactInfo?.website || leadData.website || '',
      location: leadData.contactInfo?.address || leadData.location || '',
      email: leadData.contactInfo?.email || '',
      phone: leadData.contactInfo?.phone || ''
    };

    // Init memory for manually saved lead
    MemoryService.initMemory(company.id, activeProject.id, 'Handmatige Opslag');

    const updatedProjects = projects.map(p => {
      if (p.id === activeProject.id) {
        const data = [...(p.customData || [])];
        const existsIdx = data.findIndex(d => d.name === company.name || (d.id && d.id === company.id));

        let finalData;
        if (existsIdx > -1) {
          data[existsIdx] = { ...data[existsIdx], ...company };
          finalData = data;
        } else {
          finalData = [company, ...data];
        }
        return { ...p, customData: finalData };
      }
      return p;
    });
    persistProjects(updatedProjects);
  };

  const matchesLeadTarget = (payload: any, target: { companyName?: string; companyId?: string }) => {
    const payloadName = payload?.companyName || payload?.name;
    const payloadId = payload?.companyId || payload?.id;
    if (target.companyId && payloadId && payloadId === target.companyId) return true;
    if (target.companyName && payloadName === target.companyName) return true;
    return false;
  };

  const handleDeleteLead = (target: { companyName: string; companyId?: string }) => {
    if (!activeProjectId) return;
    const companyName = target.companyName;
    // Remove from results
    const updated = { ...results };
    if (updated[activeProjectId]) {
      updated[activeProjectId] = updated[activeProjectId].filter(r => {
        if (r.agentId === 'lead-finder' && Array.isArray(r.data)) {
          r.data = r.data.filter((lead: any) => !matchesLeadTarget(lead, target));
          return r.data.length > 0;
        }
        if (r.agentId === 'researcher' && matchesLeadTarget(r.data, target)) return false;
        if (r.agentId === 'researcher-extra' && matchesLeadTarget(r.data, target)) return false;
        if (r.agentId === 'researcher-competitor' && matchesLeadTarget(r.data, target)) return false;
        if (r.agentId === 'strategy-agent' && (matchesLeadTarget(r.data, target) || (target.companyName && r.summary?.includes(target.companyName)))) return false;
        return true;
      });
      persistResults(updated);
    }
    // Remove from project customData
    const updatedProjects = projects.map(p => {
      if (p.id === activeProjectId && p.customData) {
        return {
          ...p,
          customData: p.customData.filter((c: any) => {
            if (target.companyId && c.id === target.companyId) return false;
            if (target.companyName && c.name === target.companyName) return false;
            return true;
          })
        };
      }
      return p;
    });
    persistProjects(updatedProjects);
    console.log(`[Lead] verwijderd`, target);
  };

  // ── Project Export/Import/Delete ──
  const zipImportRef = React.useRef<HTMLInputElement>(null);

  const handleExportProject = async () => {
    if (!activeProject) return;
    const projectResults = results[activeProject.id] || [];
    await ProjectExportService.exportAsZip(activeProject, projectResults);
  };

  const handleImportProject = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { project, results: importedResults, memories } = await ProjectExportService.importFromZip(file);
      // Add project
      const updatedProjects = [...projects, project];
      persistProjects(updatedProjects);
      // Add results
      const updatedResults = { ...results, [project.id]: importedResults };
      persistResults(updatedResults);
      // Restore memories
      Object.entries(memories).forEach(([companyId, mem]: [string, any]) => {
        MemoryService.hydrateMemory(companyId, project.id, mem);
      });
      setActiveProjectId(project.id);
      resetProjectModal();
      console.log(`[Project] ✅ ${project.name} geïmporteerd`);
    } catch (err) {
      console.error('[Project] Import failed:', err);
      alert('Import mislukt: ongeldig ZIP bestand');
    }
    e.target.value = '';
  };

  const handleDeleteProject = () => {
    if (!activeProject) return;
    if (!confirm(`Weet je zeker dat je project "${activeProject.name}" wilt verwijderen? Dit kan niet ongedaan worden.`)) return;
    const deletedProjectId = activeProject.id;
    const updatedProjects = projects.filter(p => p.id !== activeProject.id);
    persistProjects(updatedProjects);
    const updatedResults = { ...results };
    delete updatedResults[activeProject.id];
    persistResults(updatedResults);
    setActiveProjectId(null);
    void WorkspaceSyncService.deleteWorkspace(deletedProjectId).catch((error) => {
      console.warn(`[Sync] Workspace delete failed for ${activeProject.name}`, error);
    });
    console.log(`[Project] 🗑️ ${activeProject.name} verwijderd`);
  };

  const handleStartCall = (company: Company, strategy?: any) => {
    const resolvedCompany = resolveCompanyReference(company);
    if (!resolvedCompany) {
      console.warn('[Voice] Geen geldig bedrijf gevonden voor gesprek', company);
      return;
    }
    prepareLiveAudioContext();
    setActiveVoiceContext({ company: resolvedCompany, strategy });
    setIsVoiceMode(true);
  };

  const handleStartGlobalVoice = () => {
    prepareLiveAudioContext();
    // Pass active project context so the voice AI knows which project is selected
    setActiveVoiceContext({ company: null });
    setIsVoiceMode(true);
  };

  const handleSaveVoiceInteraction = async (payload: string | { rawTranscript: string; transcriptParts: CallTranscriptPart[] }) => {
    // Save to Company Memory/Dossier only — NOT to the chat (prevents AI re-response)
    if (activeVoiceContext?.company && activeProject) {
      const rawTranscript = typeof payload === 'string' ? payload : payload.rawTranscript;
      const transcriptLines = (typeof payload === 'string'
        ? (payload || '')
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line) => ({
              speaker: line.startsWith('Contactpersoon:') ? 'contact' as const : 'agent' as const,
              text: line
                .replace(/^Contactpersoon:\s*/, '')
                .replace(/^BDAT:\s*/, '')
                .trim()
            }))
        : payload.transcriptParts
      ).filter((part) => part.text?.trim());

      if (!transcriptLines.length && !rawTranscript?.trim()) {
        console.log('[Voice] Geen transcriptregels beschikbaar om op te slaan');
        return;
      }

      setGlobalLoadingText('Gesprek analyseren...');
      try {
        const analysis = await CallAnalysisService.analyzeLiveCall({
          project: activeProject,
          company: activeVoiceContext.company,
          transcript: transcriptLines,
          rawTranscript
        });

        MemoryService.addInteraction(activeVoiceContext.company.id, activeProject.id, {
          id: crypto.randomUUID(),
          type: 'call',
          direction: 'outbound',
          timestamp: new Date().toISOString(),
          summary: analysis.summary || `Gesprek gevoerd met ${activeVoiceContext.company.name}`,
          sentiment: analysis.sentiment,
          details: analysis
        });
        MemoryService.applyCallLearning(activeVoiceContext.company.id, activeProject.id, analysis);
        setMemoryRefreshKey((current) => current + 1);
        console.log(`[Voice] Gesprek en analyse opgeslagen in dossier van ${activeVoiceContext.company.name}`);
        return;
      } catch (error) {
        console.error('[Voice] Analyse of opslag mislukt, fallback-opslag actief:', error);
      } finally {
        setGlobalLoadingText(null);
      }

      MemoryService.addInteraction(activeVoiceContext.company.id, activeProject.id, {
        id: crypto.randomUUID(),
        type: 'call',
        direction: 'outbound',
        timestamp: new Date().toISOString(),
        summary: `Gesprek gevoerd met ${activeVoiceContext.company.name} (${new Date().toLocaleTimeString()})`,
        sentiment: 'neutral',
        details: {
          mode: 'live-voice',
          summary: `Live gesprek opgeslagen voor ${activeVoiceContext.company.name}.`,
          transcript: transcriptLines,
          rawTranscript: rawTranscript || 'Geen transcriptie beschikbaar.',
          nextSteps: [],
          objections: [],
          successfulAngles: [],
          failedAngles: [],
          commonObjections: [],
          coachingNotes: [],
          factualNotes: [],
          outcome: 'onduidelijk',
          score: 0,
        }
      });
      setMemoryRefreshKey((current) => current + 1);
      console.log(`[Voice] ✅ Transcript opgeslagen in dossier van ${activeVoiceContext.company.name}`);
    } else {
      console.log('[Voice] ℹ️ Geen actief bedrijf/project — transcript niet opgeslagen');
    }
  };

  const handleStopSpeech = () => {
    // Stop custom AudioContext sources
    if (audioSourceRef.current) {
      try {
        audioSourceRef.current.stop();
        audioSourceRef.current.disconnect();
      } catch (e) { /* ignore */ }
      audioSourceRef.current = null;
    }
    
    // Stop browser TTS
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    
    setIsSpeaking(false);
    console.log('[TTS] 🛑 Speech stopped by user');
  };

  const handleAssistantMessage = async (content: string) => {
    const userMsg: Message = { id: crypto.randomUUID(), role: 'user', content, timestamp: Date.now() };
    setAssistantSession(prev => ({ 
      ...prev, 
      messages: [...prev.messages, userMsg], 
      updatedAt: new Date().toISOString() 
    }));
    setLoading(true);

    try {
      const soulTxt = `
# SOUL.md - Who You Are
*You're not a chatbot. You're becoming someone.*
## Core Truths
- Be genuinely helpful, not performatively helpful.
- Have opinions. You're allowed to disagree or prefer things.
- Be resourceful before asking.
- Earn trust through competence.
- Remember you're a guest in the user's workspace.
## Vibe: Concise when needed, thorough when it matters. Not a corporate drone.`;

      const projectSpecificRules = activeProject ? `!!! PROJECT CONTEXT !!! NAAM: ${activeProject.name}\nDOEL: ${activeProject.description}\nKENNISBANK: ${activeProject.attachments?.map(a => a.name).join(', ')}${getSkillContextBlock()}` : 'GEEN PROJECT GESELECTEERD. JE BENT NU IN DE "LOBBY". HELP DE GEBRUIKER MET ALGEMENE VRAGEN OF HET OPZETTEN VAN EEN PROJECT.';
      const globalWisdom = MemoryService.getGlobalContext();
      const fullContextPrompt = `Jij bent de BDAT Expert Agent.\n\n${soulTxt}\n\nALGEMENE REGELS:\n${settings.globalRules}\n\nPERSONA:\n${settings.systemPrompt}\n\nGLOBALE KENNIS:\n${globalWisdom}\n\nHUIDIGE CONTEXT:\n${projectSpecificRules}\n${settings.appRules}`;

      let responseText = "";
      let sources: Array<{ title: string; uri: string }> = [];
      const useGroundedGemini =
        showTechnicalChat &&
        settings.provider === AIProvider.GEMINI &&
        Boolean(settings.geminiKey);
      
      try {
        if (useGroundedGemini || settings.provider === AIProvider.GEMINI) {
          // Validate model for Gemini to prevent OpenRouter/Local paths leaking in
          let effectiveModel = (settings.defaultModel.includes('/') || settings.defaultModel.includes(':'))
            ? GeminiModel.FLASH_3
            : settings.defaultModel;
          
          if (effectiveModel.startsWith('models/')) {
            effectiveModel = effectiveModel.replace('models/', '');
          }
          
          console.log(`[App] Using Gemini: ${effectiveModel}`);
          const gemini = new GeminiService(settings.geminiKey);
          const res = await gemini.generateText(content, fullContextPrompt, effectiveModel, true, null, settings.thinkingBudget);
          responseText = res.text;
          sources = (res.groundingChunks || [])
            .map((chunk: any) => chunk?.web ? { title: chunk.web.title, uri: chunk.web.uri } : null)
            .filter((item: any): item is { title: string; uri: string } => Boolean(item?.title && item?.uri));
        } else if (settings.provider === AIProvider.OPENROUTER) {
          console.log(`[App] Using OpenRouter: ${settings.defaultModel}`);
          if (settings.costMode === CostMode.FREE_FIRST) {
            responseText = (await OpenRouterService.generateTextFreeFirst(settings.openRouterKey, content, fullContextPrompt, settings.defaultModel)).text;
          } else if (settings.costMode === CostMode.CHEAP_PAID_FALLBACK) {
            try {
              responseText = (await OpenRouterService.generateTextFreeFirst(settings.openRouterKey, content, fullContextPrompt, settings.defaultModel)).text;
            } catch (freeError) {
              console.warn('[App] Free OpenRouter chat failed, falling back to paid auto because Cheap Paid Fallback is enabled.', freeError);
              responseText = await OpenRouterService.generateText(settings.openRouterKey, content, fullContextPrompt, OpenRouterService.PAID_AUTO_MODEL_ID);
            }
          } else {
            responseText = await OpenRouterService.generateText(settings.openRouterKey, content, fullContextPrompt, settings.defaultModel || OpenRouterService.PAID_AUTO_MODEL_ID);
          }
        } else if (settings.provider === AIProvider.OLLAMA || settings.provider === AIProvider.LMSTUDIO || settings.provider === AIProvider.LOCAL) {
          console.log(`[App] Using Local LLM (${settings.provider}): ${settings.defaultModel}`);
          responseText = await LocalLLMService.generateText(settings.localUrl, content, fullContextPrompt, settings.defaultModel, settings.localApiKey);
        }
      } catch (err: any) {
        console.error(`[AI Error] Provider: ${settings.provider}`, err);
        responseText = `⚠️ Fout bij communicatie met ${settings.provider}: ${err.message || 'Onbekende fout'}. Controleer je API-key of endpoint instellingen.`;
      }
      const modelMsg: Message = { id: crypto.randomUUID(), role: 'model', content: responseText || "...", timestamp: Date.now(), sources };
      setAssistantSession(prev => ({ 
        ...prev, 
        messages: [...prev.messages, modelMsg], 
        updatedAt: new Date().toISOString() 
      }));

      // Play Voice-over if enabled
      if (responseText && settings.autoSpeak) {
        try {
          const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
          if (audioCtx.state === 'suspended') {
            await audioCtx.resume();
          }
          
          let played = false;
          setIsSpeaking(true);
          
          try {
            if (settings.ttsProvider === TTSProvider.GEMINI && settings.geminiKey) {
              const voiceName = settings.voiceName || 'Zephyr';
              console.log(`[TTS] Fetching Gemini TTS (voice: ${voiceName})...`);
              const gemini = new GeminiService(settings.geminiKey);
              const base64Audio = await gemini.generateTTS(responseText, voiceName);
              
              if (base64Audio) {
                const pcmBytes = decodeBase64(base64Audio);
                const audioBuffer = await decodePCMAudio(pcmBytes, audioCtx, 24000, 1);
                const source = audioCtx.createBufferSource();
                source.buffer = audioBuffer;
                source.connect(audioCtx.destination);
                
                source.onended = () => {
                  setIsSpeaking(false);
                  audioSourceRef.current = null;
                };
                
                audioSourceRef.current = source;
                source.start(0);
                played = true;
                console.log('[TTS] ✅ Gemini audio playing');
              }
            }
          } catch (fetchErr) {
            console.error('[TTS] ❌ AI Voice generation failed:', fetchErr);
            setIsSpeaking(false);
          }

          if (!played) {
            console.log('[TTS] 🔄 Using Browser TTS Fallback...');
            const utterance = new SpeechSynthesisUtterance(responseText);
            utterance.lang = 'nl-NL';
            utterance.onend = () => setIsSpeaking(false);
            utterance.onerror = () => setIsSpeaking(false);
            
            const voices = window.speechSynthesis.getVoices();
            const dutchVoice = voices.find(v => v.lang.startsWith('nl'));
            if (dutchVoice) utterance.voice = dutchVoice;
            
            setIsSpeaking(true);
            window.speechSynthesis.speak(utterance);
          }
        } catch (vErr) {
          console.error('[TTS] Voice playback error:', vErr);
          setIsSpeaking(false);
        }
      }
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const getCompanyLaneKey = (payload: any) => {
    const companyId = payload?.companyId || payload?.lead?.companyId || payload?.research?.companyId;
    const companyName = payload?.companyName || payload?.name || payload?.lead?.companyName || payload?.research?.companyName || 'onbekend-bedrijf';
    return companyName.trim().toLowerCase() || companyId || 'onbekend-bedrijf';
  };

  const companyLanes = useMemo(() => {
    const lanes: Record<string, { lead?: any, research?: any, extraResearch?: any, strategy?: any }> = {};
    activeResults.forEach(r => {
      if (r.agentId === 'lead-finder' && Array.isArray(r.data)) {
        r.data.forEach((lead: any) => {
          const key = getCompanyLaneKey(lead);
          if (!lanes[key]) lanes[key] = {};
          lanes[key].lead = lead;
        });
      } else if (r.agentId === 'researcher') {
        const key = getCompanyLaneKey(r.data);
        if (!lanes[key]) lanes[key] = {};
        lanes[key].research = r.data;
      } else if (r.agentId === 'researcher-competitor') { // Special case handling
        // We log competitor data, but maybe we just want to update the view without creating a new lane entry if it exists
        const key = getCompanyLaneKey(r.data);
        if (!lanes[key]) lanes[key] = {};
        // Optionally store it on the lane if we want to show it in ResultViewer
      } else if (r.agentId === 'researcher-extra') {
        const key = getCompanyLaneKey(r.data);
        if (!lanes[key]) lanes[key] = {};
        lanes[key].extraResearch = r.data;
      } else if (r.agentId === 'strategy-agent') {
        const key = getCompanyLaneKey(r.data);
        if (!lanes[key]) lanes[key] = {};
        lanes[key].strategy = r.data;
      }
    });
    return lanes;
  }, [activeResults]);

  // Voor research modal en exclude lists
  const searchableNames = useMemo(() => {
    const names = new Set<string>();
    activeResults.forEach(r => {
      if (r.agentId === 'lead-finder' && Array.isArray(r.data)) r.data.forEach((d: any) => d.companyName && names.add(d.companyName));
    });
    if (activeProject?.customData) {
      activeProject.customData.forEach(c => names.add(c.name));
    }
    return Array.from(names);
  }, [activeResults, activeProject]);

  // Auto-save leads to project when companyLanes changes
  useEffect(() => {
    if (!activeProject || !isDataLoaded) return;
    const laneNames = Object.keys(companyLanes);
    if (laneNames.length === 0) return;

    laneNames.forEach(name => {
      const lane = companyLanes[name];
      const leadData = lane.research || lane.lead;
      if (leadData) {
        handleSaveLead(leadData);
      }
    });
  }, [Object.keys(companyLanes).length]); // eslint-disable-line

  return (
    <div
      className="flex h-screen bg-transparent text-slate-900 relative font-['Inter']"
      style={{
        ['--bdat-sidebar-width' as any]: 'clamp(15rem, 23vw, 19rem)',
      }}
    >
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-[140] bg-slate-950/50 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileNavOpen(false)}
        />
      )}
      <div className={`fixed inset-y-0 left-0 z-[150] w-[min(86vw,20rem)] lg:static lg:z-50 lg:w-[var(--bdat-sidebar-width)] flex flex-col border-r border-slate-200/80 shrink-0 select-none transform transition-transform duration-300 lg:transform-none ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} bg-[linear-gradient(180deg,color-mix(in_srgb,var(--bdat-sidebar)_88%,white)_0%,color-mix(in_srgb,var(--bdat-sidebar)_96%,var(--bdat-canvas))_100%)]`}>
        <div className="p-6 border-b border-slate-200/80 cursor-pointer hover:bg-white/40 transition-colors" onClick={() => setActiveProjectId(null)} title="Terug naar Home">
          <div className="flex items-center gap-3">
            <div className="relative w-7 h-7 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-500 flex items-center justify-center shadow-[0_14px_30px_rgba(49,95,244,0.18)]">
              <div className="w-2.5 h-2.5 rounded-full bg-white/90"></div>
            </div>
            <div>
              <h1 className="font-['Space_Mono'] font-bold text-lg tracking-[0.24em] text-slate-950">BDAT</h1>
              <p className="text-[9px] text-slate-500 uppercase tracking-[0.28em] mt-1">Light Ops</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col">
          <Sidebar
            assistantSession={assistantSession}
            onAssistantSendMessage={handleAssistantMessage}
            isProcessing={loading}
            onOpenSettings={() => setShowSettingsModal(true)}
            onOpenSkills={() => setShowSkillModal(true)}
            onOpenProjectImport={() => zipImportRef.current?.click()}
            onCreateProject={() => setShowProjectModal(true)}
            onNewChat={handleNewChat}
            onToggleVoice={handleStartGlobalVoice}
            onExpandChat={() => setShowTechnicalChat(true)}
            projects={projects}
            activeProjectId={activeProjectId}
            onProjectSelect={(id) => { setActiveProjectId(id); setMobileNavOpen(false); }}
            disableCallAgent={settings.disableCallAgent}
          />
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden relative pt-14 lg:pt-0">
        <div className="lg:hidden fixed top-0 inset-x-0 z-[130] flex h-14 items-center gap-3 border-b border-slate-200 bg-white/95 px-3 backdrop-blur">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Menu openen"
            className="rounded-xl border border-slate-300 bg-white p-2 text-slate-700 active:scale-95 transition"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
          </button>
          <span className="font-['Space_Mono'] font-bold text-sm tracking-[0.24em] text-slate-950">BDAT</span>
          <span className="ml-auto text-[9px] font-black uppercase tracking-[0.2em] text-slate-400">Light Ops</span>
        </div>
        {loading && (
          <div className="absolute top-0 left-0 right-0 h-1 z-[100] overflow-hidden">
            <div className="h-full w-full bg-gradient-to-r from-sky-300 via-blue-600 to-sky-300 animate-[shimmer_2s_infinite] bg-[length:200%_100%]"></div>
          </div>
        )}

        <div className={`flex-1 flex flex-col overflow-hidden relative transition-all duration-300 ${showTechnicalChat ? 'opacity-40 pointer-events-none' : ''}`}>
          {activeProject ? (
            <>
              <div className="min-h-24 border-b border-slate-200/80 bg-white/70 backdrop-blur-xl flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between px-5 py-5 md:px-6 xl:px-8 z-10">
                <div className="flex items-center gap-4 min-w-0">
                  <div>
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.28em]">Active Workspace</p>
                    <h2 className="mt-2 text-[1.7rem] md:text-[2rem] leading-none font-black text-slate-950 tracking-tight break-words">{activeProject.name}</h2>
                  </div>
                  {loading && <div className="hidden md:flex text-[10px] font-black text-blue-700 uppercase tracking-widest animate-pulse items-center gap-2"><i className="fas fa-sync fa-spin"></i> {globalLoadingText}</div>}
                </div>
                <div className="flex items-center gap-3 lg:gap-6 self-start lg:self-auto">
                  <button onClick={() => {
                    setProjectName(activeProject.name);
                    setProjectDesc(activeProject.description);
                    setProjectSenderEmail(activeProject.senderEmail || extractEmailFromText(activeProject.description || ''));
                    setProjectSenderContactBlock(activeProject.senderContactBlock || '');
                    setAutopilotEnabled(activeProject.autopilot?.isEnabled || false);
                    setIsEditingProject(true);
                    setShowProjectModal(true);
                  }} className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-blue-700 hover:bg-white px-5 py-2.5 rounded-2xl border border-blue-200 bg-blue-50/70 transition-all">
                    <PencilIcon /> Project Instellingen
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-5 md:px-5 xl:px-7 xl:py-7 space-y-7 custom-scrollbar bg-transparent">
                <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-4 md:gap-5 xl:gap-6">
                  {AGENT_DEFINITIONS.map(agent => (
                    <AgentCard key={agent.id} agent={agent} onClick={() => handleRunAgent(agent.type)} loading={loading} />
                  ))}
                </div>

                <div className="space-y-8">
                  <h3 className="font-black text-slate-800 text-xs uppercase tracking-[0.2em] flex items-center gap-3">
                    Lead Flow
                    <div className="w-full h-px bg-slate-300/80"></div>
                    {activeProject && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={handleExportProject} className="p-2 text-slate-400 hover:text-blue-700 hover:bg-white rounded-xl transition-colors" title="Exporteer als ZIP">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                        </button>
                        <button onClick={() => zipImportRef.current?.click()} className="p-2 text-slate-400 hover:text-emerald-700 hover:bg-white rounded-xl transition-colors" title="Importeer ZIP">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                        </button>
                        <button onClick={handleDeleteProject} className="p-2 text-slate-400 hover:text-red-500 hover:bg-white rounded-xl transition-colors" title="Verwijder project">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                        </button>
                      </div>
                    )}
                  </h3>

                  <div className="space-y-5 pb-20 md:pb-24">
                    {Object.entries(companyLanes).map(([laneKey, lane]) => (
                      <ResultViewer
                        key={laneKey}
                        result={{ agentId: 'lane-view', data: lane } as any}
                        onDeepResearch={(lead, mode) => handleRunAgent('researcher', {
                          companyName: lead.companyName || lead.name,
                          companyId: lead.companyId || lane.research?.companyId || lane.extraResearch?.companyId || lane.strategy?.companyId,
                          mode
                        })}
                        onGenerateStrategy={handleGenerateStrategy}
                        onSaveLead={handleSaveLead}
                        onDeleteLead={handleDeleteLead}
                        onStartCall={handleStartCall}
                        onCompanySelect={handleSelectCompany}
                        disableCallAgent={settings.disableCallAgent}
                      />
                    ))}

                    {Object.keys(companyLanes).length > 0 && !loading && (
                      <div className="flex justify-center pt-12">
                        <button
                          onClick={() => handleRunAgent('lead-finder', { mode: 'web', count: 5, excludeIds: searchableNames })}
                          className="px-10 py-4 bg-white/90 border border-slate-200 text-slate-900 hover:text-blue-700 hover:border-blue-200 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-[0_18px_32px_rgba(15,23,42,0.06)] flex items-center gap-3 transition-all hover:-translate-y-1 active:scale-95"
                        >
                          <PlusIcon /> Scan volgende 5 leads
                        </button>
                      </div>
                    )}

                    {Object.keys(companyLanes).length === 0 && (
                      <div className="text-center py-32 border border-slate-200 rounded-[3rem] bg-white/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
                        <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                          <i className="fas fa-search text-2xl text-blue-200"></i>
                        </div>
                        <p className="text-slate-500 font-bold uppercase text-[10px] tracking-widest">Activeer agents om de lead flow te vullen.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <NeuralHero onCreateProject={() => setShowProjectModal(true)} onStartGlobalVoice={handleStartGlobalVoice} />
          )}
        </div>

        {showTechnicalChat && (
          <div
            className="fixed top-[4.5rem] right-2 bottom-2 z-[60] flex animate-in slide-in-from-right duration-300 sm:top-4 sm:right-4 sm:bottom-4 shadow-[0_24px_60px_rgba(15,23,42,0.12)] overflow-hidden rounded-[2rem] bdat-soft-panel-strong"
            style={{ width: `${technicalChatWidth}px`, maxWidth: 'calc(100vw - 1rem)' }}
          >
            <div
              className="w-2 cursor-col-resize bg-slate-100 hover:bg-blue-200 transition-colors"
              onMouseDown={startTechnicalChatResize}
              title="Versleep om chat groter of kleiner te maken"
            />
            <button onClick={() => setShowTechnicalChat(false)} className="absolute left-3 top-4 w-10 h-10 bg-blue-600 text-white flex items-center justify-center rounded-2xl z-[70] shadow-[0_16px_30px_rgba(49,95,244,0.18)]" title="Sluit Dashboard"><XIcon /></button>
            <div className="flex-1 min-w-0">
              <ChatWindow
                session={assistantSession} 
                isProcessing={loading} 
                isSpeaking={isSpeaking}
                activeProject={activeProject} 
                activeResults={activeResults}
                dossierTargets={activeProject?.customData || []}
                selectedDossierCompanyId={chatDossierTargetId}
                onSendMessage={handleAssistantMessage} 
                onStopSpeech={handleStopSpeech}
                onToggleVoice={handleStartGlobalVoice} 
                onClone={() => { }} 
                onOpenMemory={() => {
                  const target = activeProject?.customData?.find((company) => company.id === chatDossierTargetId);
                  if (target) handleSelectCompany(target);
                }}
                onSelectDossierTarget={setChatDossierTargetId}
                onCopyToDossier={handleCopyChatResearchToDossier}
                disableCallAgent={settings.disableCallAgent}
              />
            </div>
          </div>
        )}
      </div>

      {isVoiceMode && (
        <VoiceInterface
          settings={settings} project={activeProject}
          company={activeVoiceContext?.company} strategy={activeVoiceContext?.strategy}
          onSettingsChange={(nextSettings) => {
            const normalizedSettings = normalizeSettings(nextSettings);
            setSettings(normalizedSettings);
            SettingsService.saveSettings(normalizedSettings);
            if (activeProjectId) {
              persistProjects(projects.map(p => p.id === activeProjectId ? { ...p, settings: normalizedSettings } : p));
            }
          }}
          onClose={() => { setIsVoiceMode(false); setActiveVoiceContext(null); }}
          onSaveTranscript={handleSaveVoiceInteraction}
        />
      )}

      {showSettingsModal && (
        <SettingsModal
          settings={settings}
          onSave={(newSettings) => {
            const normalizedSettings = normalizeSettings(newSettings);
            setSettings(normalizedSettings);
            SettingsService.saveSettings(normalizedSettings);
            if (activeProjectId) {
              persistProjects(projects.map(p => p.id === activeProjectId ? { ...p, settings: normalizedSettings } : p));
            }
          }}
          onClose={() => setShowSettingsModal(false)}
        />
      )}
      {showLeadFinderMode && <LeadFinderModeModal isOpen={showLeadFinderMode} onClose={() => setShowLeadFinderMode(false)} defaultQuery={activeProject?.description || ''} onRun={(mode: 'database' | 'web', query: string) => handleRunAgent('lead-finder', { mode, query })} />}
      {showResearchModal && <ResearchModal isOpen={showResearchModal} onClose={() => setShowResearchModal(false)} onRun={(term: string) => handleRunAgent('researcher', { companyName: term })} project={activeProject} foundNames={searchableNames} />}
      <input ref={zipImportRef} type="file" accept=".zip" className="hidden" onChange={handleImportProject} />
      <EmailComposerModal
        isOpen={!!emailComposerState}
        project={activeProject}
        company={emailComposerState?.company || null}
        draft={emailComposerState?.draft || null}
        isGenerating={loading && !!emailComposerState}
        isSending={isSendingEmailDraft}
        onClose={() => setEmailComposerState(null)}
        onRerun={(tone) => {
          if (!emailComposerState) return;
          handleRunAgent('email-outreach', {
            companyId: emailComposerState.company.id,
            tone
          });
        }}
        onSend={(draft) => {
          if (!emailComposerState) return Promise.resolve();
          return handleSendEmailDraft(emailComposerState.company, draft);
        }}
      />

      <SkillModal
        isOpen={showSkillModal}
        onClose={() => setShowSkillModal(false)}
        selectedSkills={activeSkills as Skill[]}
        onSaveSkills={handleSaveSelectedSkills}
      />

      {/* UPDATE: Pass handleRunAgent to CompanyDetailView */}
      {selectedCompany && activeProject && (
        <CompanyDetailView
          company={selectedCompany}
          project={activeProject}
          onClose={() => setSelectedCompany(null)}
          onRefresh={() => { }}
          memoryRefreshKey={memoryRefreshKey}
          researchResults={activeResults}
          onUpdate={(c) => {
            persistProjects(projects.map(p => p.id === activeProject.id ? { ...p, customData: (p.customData || []).map(d => d.id === c.id ? c : d) } : p));
          }}
          onRunAgent={handleRunAgent}
          disableCallAgent={settings.disableCallAgent}
          onImportDossier={(dossier: any) => {
            // Merge imported research results into global results
            if (dossier.researchResults && Array.isArray(dossier.researchResults)) {
              const currentResults = results[activeProject.id] || [];
              // Simple merge: add new results if ID doesn't exist
              const newResults = [...dossier.researchResults.filter((r: any) => !currentResults.find((cr: any) => cr.id === r.id)), ...currentResults];
              persistResults({ ...results, [activeProject.id]: newResults });
            }

            // Update Company Data if changed
            if (dossier.company) {
              const currentCompany = activeProject.customData?.find(c => c.id === dossier.company.id);
              if (currentCompany && JSON.stringify(currentCompany) !== JSON.stringify(dossier.company)) {
                persistProjects(projects.map(p => p.id === activeProject.id ? {
                  ...p,
                  customData: (p.customData || []).map(d => d.id === dossier.company.id ? dossier.company : d)
                } : p));
                handleSelectCompany(dossier.company); // Refresh view
              }
            }
          }}
        />
      )}

      {showProjectModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] backdrop-blur-md p-2 sm:p-4">
          <div className="bg-white p-4 sm:p-6 lg:p-7 rounded-[1.75rem] sm:rounded-[2.25rem] w-full max-w-[min(64rem,calc(100vw-1rem))] border border-[color:var(--bdat-border)] text-slate-900 shadow-[0_28px_80px_rgba(15,23,42,0.18)] max-h-[92vh] overflow-y-auto custom-scrollbar">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 tracking-tight">{isEditingProject ? 'Bewerk Werkruimte' : 'Start Nieuw Project'}</h2>

            <div className="space-y-6 sm:space-y-8">
              <div className="space-y-5 sm:space-y-6">
                <div className="space-y-3">
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Project Naam</label>
                  <input
                    className="w-full bdat-input-surface px-4 py-3.5 rounded-2xl outline-none focus:ring-2 focus:ring-[#5249f3]/25 transition-all placeholder:text-slate-400 font-['Space_Mono'] text-sm"
                    placeholder="Bijv. Outreach Q4"
                    value={projectName}
                    onChange={e => setProjectName(e.target.value)}
                  />
                </div>

                <div className="space-y-3">
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Doelstelling & Context</label>
                  <textarea
                    className="w-full bdat-input-surface px-4 py-3.5 rounded-2xl outline-none h-28 sm:h-32 focus:ring-2 focus:ring-[#5249f3]/25 transition-all resize-none placeholder:text-slate-400 text-sm"
                    placeholder="Omschrijf het doel van dit project..."
                    value={projectDesc}
                    onChange={e => setProjectDesc(e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-3">
                    <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Van E-mail</label>
                    <input
                      className="w-full bdat-input-surface px-4 py-3.5 rounded-2xl outline-none focus:ring-2 focus:ring-[#5249f3]/25 transition-all placeholder:text-slate-400 font-['Space_Mono'] text-sm"
                      placeholder="bijv. bookings@jouwdomein.nl"
                      value={projectSenderEmail}
                      onChange={e => setProjectSenderEmail(e.target.value)}
                    />
                  </div>

                  <div className="space-y-3">
                    <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Contactblok Voor E-mail/PDF</label>
                    <textarea
                      className="w-full bdat-input-surface px-4 py-3.5 rounded-2xl outline-none h-28 sm:h-32 focus:ring-2 focus:ring-[#5249f3]/25 transition-all resize-none placeholder:text-slate-400 text-sm"
                      placeholder={"Bijv.\nDuo D'Major\nbookings@duodmajor.nl\n+31 6 12345678\nwww.duodmajor.nl"}
                      value={projectSenderContactBlock}
                      onChange={e => setProjectSenderContactBlock(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4 border-t border-[color:var(--bdat-border)]">
                <div className="space-y-3">
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Lead Database (CSV)</label>
                  <button
                    onClick={() => csvInputRef.current?.click()}
                    className={`w-full border-2 border-dashed rounded-2xl p-4 sm:p-5 text-center transition-all flex flex-col items-center gap-2 ${csvFile ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 hover:bg-slate-50'}`}
                  >
                    <CsvIcon />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
                      {csvFile ? csvFile.name : 'Selecteer CSV'}
                    </span>
                  </button>
                  <input type="file" ref={csvInputRef} accept=".csv" className="hidden" onChange={e => setCsvFile(e.target.files?.[0] || null)} title="Upload CSV" />
                </div>

                <div className="space-y-3">
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Klant Identiteit (Foto)</label>
                  <button
                    onClick={() => clientPhotoInputRef.current?.click()}
                    className={`w-full border-2 border-dashed rounded-2xl p-4 sm:p-5 text-center transition-all flex flex-col items-center gap-2 ${clientPhotoFile ? 'border-indigo-400 bg-indigo-50' : 'border-slate-300 hover:bg-slate-50'}`}
                  >
                    <UserCircleIcon />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
                      {clientPhotoFile ? clientPhotoFile.name : 'Selecteer Foto'}
                    </span>
                  </button>
                  <input type="file" ref={clientPhotoInputRef} accept="image/*" className="hidden" onChange={e => setClientPhotoFile(e.target.files?.[0] || null)} title="Upload Klant Foto" />
                </div>
              </div>

              <div className="space-y-3 pt-4 border-t border-[color:var(--bdat-border)]">
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Kennisbank (PDF/DOC)</label>
                <button
                  onClick={() => kbFilesInputRef.current?.click()}
                  className={`w-full border-2 border-dashed rounded-2xl p-6 sm:p-8 text-center transition-all flex flex-col items-center gap-3 ${kbFiles.length > 0 ? 'border-indigo-400 bg-indigo-50' : 'border-slate-300 hover:bg-slate-50'}`}
                >
                  <BookIcon />
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">
                    {kbFiles.length > 0 ? `${kbFiles.length} bestanden geselecteerd` : 'Upload Context Bestanden'}
                  </span>
                  <span className="text-[8px] text-slate-400 uppercase">PDF, TXT, JSON, DOC</span>
                </button>
                <input type="file" ref={kbFilesInputRef} multiple className="hidden" onChange={e => setKbFiles(Array.from(e.target.files || []))} title="Upload Kennisbank Bestanden" />

                {kbFiles.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                    {kbFiles.map((f, i) => (
                      <div key={i} className="bg-slate-50 border border-slate-200 p-2 rounded-lg flex items-center justify-between text-[8px] text-slate-600 font-mono">
                        <span className="truncate flex-1">{f.name}</span>
                        <button onClick={() => setKbFiles(kbFiles.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500 ml-2" title="Verwijder bestand"><XIcon /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-4 pt-6 border-t border-[color:var(--bdat-border)]">
                <div className="flex items-center justify-between bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div>
                    <h4 className="text-xs font-bold text-slate-900 uppercase tracking-widest flex items-center gap-2">
                      <i className="fas fa-robot text-indigo-400"></i> Workflow Autopilot
                    </h4>
                    <p className="text-[9px] text-slate-500 mt-1">Automatisch Research & Emails starten voor nieuwe leads.</p>
                  </div>
                  <div className="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
                    <input type="checkbox" name="toggle" id="autopilot-toggle" className={`toggle-checkbox absolute block w-5 h-5 rounded-full bg-white border-4 appearance-none cursor-pointer transition-all duration-300 ${autopilotEnabled ? 'right-0 border-indigo-500' : 'left-0 border-[#ccc]'}`}
                      checked={autopilotEnabled}
                      onChange={() => setAutopilotEnabled(!autopilotEnabled)}
                      title="Toggle Autopilot"
                    />
                    <label htmlFor="autopilot-toggle" className={`toggle-label block overflow-hidden h-5 rounded-full cursor-pointer transition-colors duration-300 ${autopilotEnabled ? 'bg-indigo-600' : 'bg-slate-600'}`}></label>
                  </div>
                </div>
              </div>

              {isEditingProject && activeProject && (
                <div className="space-y-4 pt-6 border-t border-[color:var(--bdat-border)]">
                  <div className="flex items-center justify-between bg-slate-50 p-4 rounded-xl border border-slate-200 gap-4 flex-col sm:flex-row">
                    <div>
                      <h4 className="text-xs font-bold text-slate-900 uppercase tracking-widest">Project Backup</h4>
                      <p className="text-[9px] text-slate-500 mt-1">Exporteer of importeer de volledige werkruimte inclusief resultaten en dossiers.</p>
                    </div>
                    <div className="flex w-full sm:w-auto items-center gap-3">
                      <button
                        onClick={handleExportProject}
                        className="flex-1 sm:flex-none px-4 py-3 rounded-xl border border-indigo-400/30 bg-indigo-500/10 text-[10px] font-black uppercase tracking-widest text-indigo-200 hover:bg-indigo-500/20 transition-colors"
                      >
                        Exporteer Project
                      </button>
                      <button
                        onClick={() => zipImportRef.current?.click()}
                        className="flex-1 sm:flex-none px-4 py-3 rounded-xl border border-emerald-400/30 bg-emerald-500/10 text-[10px] font-black uppercase tracking-widest text-emerald-200 hover:bg-emerald-500/20 transition-colors"
                      >
                        Importeer Project
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row justify-end items-center gap-4 pt-8">
                <button
                  onClick={resetProjectModal}
                  className="text-[10px] text-slate-500 font-black uppercase tracking-widest hover:text-slate-900 transition-colors order-2 sm:order-1"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleSaveProject}
                  disabled={isProcessingFile || !projectName}
                  className="w-full sm:w-auto bdat-accent-button disabled:opacity-50 px-8 sm:px-10 py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-[0_18px_36px_rgba(49,95,244,0.18)] active:scale-95 transition-all order-1 sm:order-2 flex items-center justify-center gap-2"
                >
                  {isProcessingFile && <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>}
                  {isEditingProject ? 'Wijzigingen Opslaan' : 'Project Opslaan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}} />
    </div>
  );
}
